/* AETR motor — pravila se računaju na PERIODIMA (rad ↔ odmor), ne po kalendarskom danu.
 *
 * Zašto: dnevni odmor po pravilu pada preko ponoći (npr. 19:33 → 04:38 sledećeg dana).
 * Kad se odmor traži unutar jednog kalendarskog dana, algoritam nađe najdužu pauzu
 * TOKOM rada (npr. 01:06) i prijavi je kao "nedovoljan dnevni odmor" — što je netačno.
 *
 * Referenca: AETR čl. 6 (vreme upravljanja), čl. 7 (pauze), čl. 8 (odmori),
 * uz EU 561/2006 koja se primenjuje istovetno.
 *
 * Svaki prekršaj nosi i interval (Pocetak/Kraj) da bi dijagram mogao da ga oboji.
 */
import { saKaznom, nivoDnevnaVoznja, nivoNedeljnaVoznja, nivoDvonedeljnaVoznja, nivoDnevniOdmor } from './kazneIznosi';
import { PROPISI, SVI_PROPISI, limitiZa, pravilaZa, propisPravila, kategorijaZa, clanZa } from './propisi';
import { uZonu, fmtDatum as zFmtDatum, fmtDatumVreme as zFmtDatumVreme } from './vreme';

const MIN = 60 * 1000;
const H = 60 * MIN;

/* Limiti NE stoje više ovde — drži ih registar propisa (propisi.js), jer zavise od
   toga koji se propis kontroliše. Ovo je podrazumevani skup (AETR 2010 + domaći),
   da bilo koji stari pozivalac i dalje radi. */
export const LIMITI = limitiZa(SVI_PROPISI);

const jeOdmor  = (v) => v === 'break/rest' || v === 'shortrest';
const jeVoznja = (v) => v === 'driving';
const jeRad    = (v) => v === 'work';

/* Domaći limiti (kratka pauza, noćni prozor, 14h/10h) takođe dolaze iz registra —
   vidi PROPISI.RS_2015. Ova imena ostaju kao prečice do podrazumevanih
   vrednosti; pravila ih čitaju iz ctx.L, pa se menjaju sa izborom propisa. */
const D = PROPISI.RS_2015.limiti;
const KRATKA_PAUZA = D.kratkaPauza;
const NOCNO_OD = D.nocnoOdSat;
const NOCNO_DO = D.nocnoDoSat;
const RADNO_VREME_NOCNO = D.radnoVremeNocno;
const RADNO_VREME_MAX   = D.radnoVremeMax;

const preklop = (a1, a2, b1, b2) => Math.max(0, Math.min(a2, b2) - Math.max(a1, b1));

/** Koliko intervala pada u noćno vreme (00:00–04:00, čl. 40). */
function nocniPreklop(od, doo) {
    /* Prozor 00:00–04:00 je LOKALNO vreme (čl. 40), ne UTC.

       Dok se računalo u UTC-u, prozor je po Beogradu zapravo bio 02:00–06:00
       leti i 01:00–05:00 zimi — pa se noćni rad i prepoznavao na pogrešnim
       satima, a s njim i stroži limit radnog vremena od 10h (čl. 4 st. 5).

       Zato se oba kraja prvo prebace u zonu, pa se preklop računa nad zidnim
       satom. Trajanje ostaje tačno jer se obe granice pomeraju isto. */
    const odL = uZonu(od);
    const dooL = uZonu(doo);
    let ukupno = 0;
    const prvi = new Date(odL);
    let dan = Date.UTC(prvi.getUTCFullYear(), prvi.getUTCMonth(), prvi.getUTCDate());
    for (; dan <= dooL; dan += 24 * H) {
        ukupno += preklop(odL, dooL, dan + NOCNO_OD * H, dan + NOCNO_DO * H);
    }
    return ukupno;
}

/** Aktivnosti isečene na granice perioda, hronološki. */
function segmentiUPeriodu(aktivnosti, od, doo) {
    return aktivnosti
        .map(a => {
            const s = Math.max(od, new Date(a.StartTime).getTime());
            const e = Math.min(doo, new Date(a.EndTime).getTime());
            return { v: a.Value, od: s, do: e, t: e - s };
        })
        .filter(x => x.t > 0)
        .sort((a, b) => a.od - b.od);
}

/** Radno vreme = vožnja + ostali rad + pauze kraće od 15 min. */
function radnoVreme(segmenti) {
    return segmenti.reduce((acc, s) => {
        if (jeVoznja(s.v) || jeRad(s.v)) return acc + s.t;
        if (jeOdmor(s.v) && s.t < KRATKA_PAUZA) return acc + s.t;
        return acc;
    }, 0);
}

/* ── formatiranje (isti stil poruka kao do sada) ─────────────────────────── */

function fmtTrajanje(ms) {
    const sati = Math.floor(ms / H);
    const min = Math.floor((ms % H) / MIN);
    return `${String(sati).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/* PRIKAZ ide u ZONI TAHOGRAFA (vidi vreme.js), ne u zoni pregledača.
   Ranije je stajalo `d.getHours()` — lokalno vreme mašine na kojoj je otvoren
   pretraživač. Bilo je tačno samo dok operater sedi u Beogradu; iz Ljubljane ili
   sa servera u drugoj zoni isti prekršaj je dobijao drugo vreme. */
const fmtDatum = (ms) => zFmtDatum(ms);
const fmtDatumVreme = (ms) => zFmtDatumVreme(ms);

/* ── periodi ─────────────────────────────────────────────────────────────── */

/**
 * Aktivnosti jednog vozača → naizmenični periodi RAD / ODMOR.
 * Odmor kraći od 9h NE prekida radni period (to je pauza unutar rada), pa svaki
 * ODMOR period u rezultatu jeste kandidat za dnevni odmor.
 */
export function napraviPeriode(aktivnosti, L = LIMITI) {
    const sortirano = [...aktivnosti]
        .filter(a => a.StartTime && a.EndTime)
        .sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));

    const sirovi = [];
    let tek = null;

    for (const a of sortirano) {
        const od = new Date(a.StartTime).getTime();
        const doo = new Date(a.EndTime).getTime();
        if (!(doo > od)) continue;
        const tip = jeOdmor(a.Value) ? 'ODMOR' : 'RAD';

        if (!tek || tek.tip !== tip || od > tek.do) {
            /* rupa u podacima (nema zapisa) tretira se kao odmor —
               kartica nije bila u uređaju, pa se ne može računati kao rad */
            if (tek && od > tek.do) {
                sirovi.push(tek);
                sirovi.push({ tip: 'ODMOR', od: tek.do, do: od, voznja: 0, rad: 0, raspolozivost: 0, rupa: true });
                tek = null;
            }
            if (tek) sirovi.push(tek);
            tek = { tip, od, do: doo, voznja: 0, rad: 0, raspolozivost: 0 };
        } else {
            tek.do = Math.max(tek.do, doo);
        }

        const trajanje = doo - od;
        if (jeVoznja(a.Value)) tek.voznja += trajanje;
        else if (jeRad(a.Value)) tek.rad += trajanje;
        else if (a.Value === 'availability') tek.raspolozivost += trajanje;
    }
    if (tek) sirovi.push(tek);

    /* Odmor kraći od `zatvaraPeriod` ne prekida radni period — to je pauza unutar
       rada. Prag je u registru propisa (danas 9h, Tachospeed koristi 7h po
       EC 3759/2011 — vidi propisi.js).

       Izuzetak je POSLEDNJI period: ako podaci prestaju u toku odmora (npr. odmor
       počne u 19:32 a fajl ide do ponoći), taj odmor je nepotpun — ne sme se
       spojiti u rad, jer bi radni period izgledao kao 19h i lažno prijavio
       "nema dnevnog odmora u 24h". */
    const spojeni = [];
    sirovi.forEach((p, idx) => {
        const prethodni = spojeni[spojeni.length - 1];
        const trajanje = p.do - p.od;
        // prvi i poslednji odmor mogu biti odsečeni granicom podataka
        const poslednji = idx === sirovi.length - 1 || idx === 0;
        if (p.tip === 'ODMOR' && poslednji && trajanje < L.zatvaraPeriod) {
            spojeni.push({ ...p, nepotpun: true });
        } else if (p.tip === 'ODMOR' && trajanje < L.zatvaraPeriod && prethodni && prethodni.tip === 'RAD') {
            prethodni.do = p.do;
        } else if (prethodni && prethodni.tip === p.tip) {
            prethodni.do = p.do;
            prethodni.voznja += p.voznja;
            prethodni.rad += p.rad;
            prethodni.raspolozivost += p.raspolozivost;
        } else {
            spojeni.push({ ...p });
        }
    });

    /* Prvi i poslednji odmor su odsečeni granicom podataka — njihovo trajanje je
       samo donja granica. Odmor koji na 02.05. u ponoć traje 32h možda se
       nastavlja i postaje pun nedeljni odmor, pa se ne sme oceniti kao skraćen. */
    if (spojeni.length) {
        if (spojeni[0].tip === 'ODMOR') spojeni[0].nepotpun = true;
        const zadnji = spojeni[spojeni.length - 1];
        if (zadnji.tip === 'ODMOR') zadnji.nepotpun = true;
    }

    return spojeni.map(p => ({ ...p, trajanje: p.do - p.od }));
}

/** ISO nedelja — za nedeljne i dvonedeljne limite vožnje.
 *  Granica nedelje je LOKALNA (ponedeljak 00:00 po zidnom satu), kao u kontrolnom
 *  izveštaju — zato ms prvo ide kroz uZonu. */
function isoNedeljaKljuc(ms) {
    const d = new Date(uZonu(ms));
    const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const dan = x.getUTCDay() || 7;
    x.setUTCDate(x.getUTCDate() + 4 - dan);
    const pocetakGodine = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
    const nedelja = Math.ceil((((x - pocetakGodine) / 86400000) + 1) / 7);
    return x.getUTCFullYear() * 100 + nedelja;
}

/* ── pravila ─────────────────────────────────────────────────────────────── */

/** Čl. 8 — dnevni odmor. Gleda RADNE periode i odmor koji ih zatvara. */
function praviloDnevniOdmor(periodi, kontekst) {
    const L = kontekst.L;
    const out = [];
    let skraceniPosleNedeljnog = 0;

    periodi.forEach((p, i) => {
        if (p.tip === 'ODMOR') {
            if (p.nepotpun) {
                /* Odsečen granicom podataka — ne ocenjuje se, ali ako je već
                   dostigao 24h sigurno je nedeljni pa resetuje brojač skraćenih. */
                if (p.trajanje >= L.nedeljniOdmorSkraceni) skraceniPosleNedeljnog = 0;
                return;
            }
            if (p.trajanje >= L.nedeljniOdmorSkraceni) skraceniPosleNedeljnog = 0;
            else if (p.trajanje < L.dnevniOdmor) {
                skraceniPosleNedeljnog++;
                if (skraceniPosleNedeljnog > L.skracenihIzmedjuNedeljnih) {
                    out.push(kontekst.prekrsaj(
                        `Prekršaj dana ${fmtDatum(p.do)}: prekoračen broj skraćenih dnevnih odmora `
                        + `(AETR čl. 8 — najviše ${L.skracenihIzmedjuNedeljnih} između dva nedeljna odmora, ovo je ${skraceniPosleNedeljnog}.). `
                        + `Odmor ${fmtDatumVreme(p.od)} – ${fmtDatumVreme(p.do)}, trajao ${fmtTrajanje(p.trajanje)}!`,
                        nivoDnevniOdmor(p.trajanje), p.od, p.do,
                        { Tip: 'Dnevni odmor', Podtip: 'Broj skraćenih', Vreme: p.do, Trajanje: p.trajanje,
                          Manjak: L.dnevniOdmor - p.trajanje }));
                }
            }
            return;
        }

        // RAD period: dnevni odmor mora biti završen u roku od 24h od kraja prethodnog odmora
        const sledeci = periodi[i + 1];
        /* Poslednji radni period (ili onaj koji zatvara nepotpun odmor) se ne
           ocenjuje — podaci prosto prestaju, odmor je možda tek počeo. */
        const poslednji = i === periodi.length - 1 || (sledeci && sledeci.nepotpun);

        if (!poslednji && p.trajanje > L.najduziRadniPeriod) {
            /* Odmor je možda i uzet, ali prekasno: u 24h prozoru (od kraja
               prethodnog odmora) ostalo je manje od 9h, pa se odmor ne završava
               u roku. Kazna se stepenuje po tome koliko je odmora STALO u prozor. */
            const rok = p.od + L.najduziRadniPeriod;          // najkasniji početak odmora
            const kasnjenje = p.do - rok;
            const odmorUProzoru = 24 * H - p.trajanje;
            const uzeo = sledeci && sledeci.tip === 'ODMOR'
                ? ` Odmor je započet ${fmtDatumVreme(sledeci.od)} i trajao ${fmtTrajanje(sledeci.trajanje)}, ali je u 24h prozor stalo samo ${fmtTrajanje(Math.max(0, odmorUProzoru))}.`
                : '';
            out.push(kontekst.prekrsaj(
                `Prekršaj dana ${fmtDatum(p.do)}: dnevni odmor nije završen u roku od 24h `
                + `(AETR čl. 8 — u 24h od kraja prethodnog odmora mora se završiti novi odmor od najmanje ${fmtTrajanje(L.dnevniOdmorSkraceni)}, `
                + `pa radni period ne sme biti duži od ${fmtTrajanje(L.najduziRadniPeriod)}). `
                + `Radni period ${fmtDatumVreme(p.od)} – ${fmtDatumVreme(p.do)} trajao ${fmtTrajanje(p.trajanje)}, `
                + `odmor je morao početi najkasnije ${fmtDatumVreme(rok)} — kasnio ${fmtTrajanje(kasnjenje)}.${uzeo}`,
                nivoDnevniOdmor(Math.max(0, odmorUProzoru)), p.od, p.do,
                { Tip: 'Dnevni odmor', Podtip: 'Nije u 24h', Vreme: p.do,
                  Trajanje: sledeci && sledeci.tip === 'ODMOR' ? sledeci.trajanje : null,
                  Prekoracenje: kasnjenje,
                  Manjak: sledeci && sledeci.tip === 'ODMOR' && sledeci.trajanje < L.dnevniOdmor
                          ? L.dnevniOdmor - sledeci.trajanje : null }));
        }

        /* Odmor koji zatvara radni period je po konstrukciji ≥ 9h. Izuzetak je
           poslednji period u podacima — tada odmor još nije ni započeo/završen,
           pa se ne prijavljuje (podaci prosto prestaju). */
        if (!poslednji && sledeci && sledeci.tip === 'ODMOR'
            && sledeci.trajanje < L.dnevniOdmorSkraceni) {
            out.push(kontekst.prekrsaj(
                `Prekršaj dana ${fmtDatum(sledeci.do)}: nedovoljan dnevni odmor `
                + `(AETR čl. 8 — najmanje ${fmtTrajanje(L.dnevniOdmor)}, skraćeni ${fmtTrajanje(L.dnevniOdmorSkraceni)}). `
                + `Odmor ${fmtDatumVreme(sledeci.od)} – ${fmtDatumVreme(sledeci.do)}, trajao ${fmtTrajanje(sledeci.trajanje)}!`,
                nivoDnevniOdmor(sledeci.trajanje), sledeci.od, sledeci.do,
                { Tip: 'Dnevni odmor', Podtip: 'Nedovoljan', Vreme: sledeci.do, Trajanje: sledeci.trajanje,
                  Manjak: L.dnevniOdmorSkraceni - sledeci.trajanje }));
        }
    });

    return out;
}

/** Čl. 6 — dnevno vreme upravljanja: 9h, izuzetno 10h najviše 2× nedeljno. */
function praviloDnevnaVoznja(periodi, kontekst) {
    const L = kontekst.L;
    const out = [];
    const produzenihUNedelji = {};

    periodi.filter(p => p.tip === 'RAD' && p.voznja > 0).forEach(p => {
        const nedelja = isoNedeljaKljuc(p.od);
        if (p.voznja <= L.dnevnaVoznja) return;

        produzenihUNedelji[nedelja] = (produzenihUNedelji[nedelja] || 0) + 1;
        const redniProduzeni = produzenihUNedelji[nedelja];

        if (p.voznja > L.dnevnaVoznjaProduzena) {
            out.push(kontekst.prekrsaj(
                `Prekršaj dana ${fmtDatum(p.do)}: prekoračeno dnevno vreme vožnje `
                + `(AETR čl. 6 — najviše ${fmtTrajanje(L.dnevnaVoznja)}, izuzetno ${fmtTrajanje(L.dnevnaVoznjaProduzena)} do ${L.produzenihNedeljno} puta nedeljno). `
                + `Vožnja ${fmtTrajanje(p.voznja)}!`,
                nivoDnevnaVoznja(p.voznja - L.dnevnaVoznja, L.dnevnaVoznja), p.od, p.do,
                { Tip: 'Vreme vožnje', Podtip: 'Dnevno', Vreme: p.do, Trajanje: p.voznja,
                  Prekoracenje: p.voznja - L.dnevnaVoznjaProduzena }));
        } else if (redniProduzeni > L.produzenihNedeljno) {
            out.push(kontekst.prekrsaj(
                `Prekršaj dana ${fmtDatum(p.do)}: prekoračen broj produženih dana vožnje `
                + `(AETR čl. 6 — vožnja do ${fmtTrajanje(L.dnevnaVoznjaProduzena)} dozvoljena je najviše ${L.produzenihNedeljno} puta nedeljno, ovo je ${redniProduzeni}. put). `
                + `Vožnja ${fmtTrajanje(p.voznja)}!`,
                nivoDnevnaVoznja(p.voznja - L.dnevnaVoznja, L.dnevnaVoznja), p.od, p.do,
                { Tip: 'Vreme vožnje', Podtip: 'Broj produženih', Vreme: p.do, Trajanje: p.voznja,
                  Prekoracenje: p.voznja - L.dnevnaVoznja }));
        }
    });

    return out;
}

/** Čl. 7 — posle 4h30 vožnje pauza 45 min (ili 15 min + 30 min). */
function praviloPauza(aktivnosti, kontekst) {
    const L = kontekst.L;
    const out = [];
    const sortirano = [...aktivnosti]
        .filter(a => a.StartTime && a.EndTime)
        .sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));

    let voznjaOdPauze = 0;
    let pocetakVoznje = null;
    let pauzaPrvi = 0;      // iskorišćen prvi deo podeljene pauze (≥15 min)
    let ukupnoPauza = 0;
    let prijavljeno = false;

    const reset = () => { voznjaOdPauze = 0; pocetakVoznje = null; pauzaPrvi = 0; ukupnoPauza = 0; prijavljeno = false; };

    for (const a of sortirano) {
        const od = new Date(a.StartTime).getTime();
        const doo = new Date(a.EndTime).getTime();
        const trajanje = doo - od;
        if (trajanje <= 0) continue;

        if (jeVoznja(a.Value)) {
            if (pocetakVoznje === null) pocetakVoznje = od;
            voznjaOdPauze += trajanje;
            ukupnoPauza = 0;                 // vožnja prekida odmor u toku
            if (voznjaOdPauze > L.neprekidnaVoznja && !prijavljeno) {
                prijavljeno = true;
                out.push(kontekst.prekrsaj(
                    `Prekršaj dana ${fmtDatum(doo)}: prekoračena vožnja bez propisane pauze `
                    + `(AETR čl. 7 — posle ${fmtTrajanje(L.neprekidnaVoznja)} vožnje sledi pauza od ${fmtTrajanje(L.pauza)}, ili 15min + 30min). `
                    + `Vožnja ${fmtTrajanje(voznjaOdPauze)}!`,
                    3, pocetakVoznje, doo,
                    { Tip: 'Pauza', Podtip: '4h30 bez pauze', Vreme: doo, Trajanje: voznjaOdPauze,
                      Prekoracenje: voznjaOdPauze - L.neprekidnaVoznja }));
            }
            continue;
        }

        if (jeOdmor(a.Value)) {
            ukupnoPauza += trajanje;
            /* Puna pauza (45 min) ili drugi deo podeljene (≥30 min posle ≥15 min)
               resetuje brojač neprekidne vožnje. */
            if (ukupnoPauza >= L.pauza
                || (pauzaPrvi >= L.pauzaPrvi && ukupnoPauza >= L.pauzaDrugi)) {
                reset();
            } else if (ukupnoPauza >= L.pauzaPrvi) {
                pauzaPrvi = ukupnoPauza;
            }
            continue;
        }

        // rad / raspoloživost ne broje se u vožnju, ali prekidaju pauzu
        ukupnoPauza = 0;
    }

    return out;
}

/**
 * Odmori ≥24h → nedeljni odmori. Dva duga odmora razdvojena kraćim radom
 * (npr. odmor 29h, pa 9h vožnje, pa odmor 27h) nisu dva nedeljna odmora nego
 * jedan produžen vikend — spajaju se u blok, a merodavan je NAJDUŽI odmor u
 * bloku, jer se on jedini uzima u obzir kao nedeljni.
 */
function nedeljniOdmori(odmori, L) {
    const dugi = odmori.filter(p => p.trajanje >= L.nedeljniOdmorSkraceni);
    const blokovi = [];
    dugi.forEach(p => {
        const prethodni = blokovi[blokovi.length - 1];
        if (prethodni && p.od - prethodni.krajBloka < 24 * H) {
            prethodni.krajBloka = p.do;
            if (p.trajanje > prethodni.trajanje) {
                prethodni.od = p.od; prethodni.do = p.do;
                prethodni.trajanje = p.trajanje; prethodni.nepotpun = !!p.nepotpun;
            }
        } else {
            blokovi.push({ od: p.od, do: p.do, trajanje: p.trajanje, krajBloka: p.do, nepotpun: !!p.nepotpun });
        }
    });
    return blokovi;
}

/** Čl. 8 — nedeljni odmor: 45h (skraćeni 24h) i najkasnije posle 6×24h.
 *  Skraćen nedeljni odmor SAM PO SEBI nije prekršaj — dozvoljen je jednom u dve
 *  uzastopne nedelje i mora se nadoknaditi u naredne tri nedelje, pridodat
 *  odmoru od najmanje 9h. Prekršaj je: dva skraćena za redom, ili nenadoknađen. */
function praviloNedeljniOdmor(periodi, kontekst, dateTo) {
    const L = kontekst.L;
    const out = [];
    /* Odsečeni odmori se ZADRŽAVAJU u listi (postoje i računaju se za razmak od
       6×24h), ali se ne ocenjuju kao skraćeni — trajanje im nije konačno. */
    const odmori = periodi.filter(p => p.tip === 'ODMOR');
    const nedeljni = nedeljniOdmori(odmori, L);
    const krajPeriodaMs = dateTo instanceof Date ? dateTo.getTime()
        : (dateTo ? new Date(dateTo).getTime() : (periodi.length ? periodi[periodi.length - 1].do : null));

    nedeljni.forEach((p, idx) => {
        if (p.trajanje >= L.nedeljniOdmor) return;
        // odmor odsečen granicom perioda može se nastaviti — ne ocenjuje se
        if (p.nepotpun) return;
        const manjak = L.nedeljniOdmor - p.trajanje;
        const prethodni = nedeljni[idx - 1];

        // dva skraćena nedeljna odmora za redom (prethodni mora biti potpun podatak)
        if (prethodni && !prethodni.nepotpun && prethodni.trajanje < L.nedeljniOdmor) {
            out.push(kontekst.prekrsaj(
                `Prekršaj dana ${fmtDatum(p.do)}: dva skraćena nedeljna odmora za redom `
                + `(AETR čl. 8 — u dve uzastopne nedelje potrebna su dva redovna odmora, ili jedan redovni i jedan skraćen). `
                + `Prethodni ${fmtDatumVreme(prethodni.od)} – ${fmtDatumVreme(prethodni.do)} (${fmtTrajanje(prethodni.trajanje)}), `
                + `ovaj ${fmtDatumVreme(p.od)} – ${fmtDatumVreme(p.do)} (${fmtTrajanje(p.trajanje)})!`,
                2, p.od, p.do,
                { Tip: 'Nedeljni odmor', Podtip: 'Dva skraćena za redom', Vreme: p.do,
                  Trajanje: p.trajanje, Manjak: manjak }));
            return;
        }

        /* Nadoknada: en bloc, pridodata odmoru od najmanje 9h, najkasnije do kraja
           treće nedelje koja sledi. Ako period izveštaja još nije prošao — ne
           prijavljuje se (rok nije istekao). */
        const rok = p.do + 3 * 7 * 24 * H;
        const nadoknadjen = odmori.some(o =>
            o.od > p.do && o.od <= rok && o.trajanje >= L.dnevniOdmorSkraceni + manjak);

        if (!nadoknadjen && krajPeriodaMs != null && krajPeriodaMs >= rok) {
            out.push(kontekst.prekrsaj(
                `Prekršaj dana ${fmtDatum(p.do)}: nenadoknađen skraćen nedeljni odmor `
                + `(AETR čl. 8 — manjak se nadoknađuje u celini, uz odmor od najmanje ${fmtTrajanje(L.dnevniOdmorSkraceni)}, najkasnije do kraja treće naredne nedelje). `
                + `Odmor ${fmtDatumVreme(p.od)} – ${fmtDatumVreme(p.do)} trajao ${fmtTrajanje(p.trajanje)}, `
                + `nadoknada ${fmtTrajanje(manjak)} nije uzeta do ${fmtDatum(rok)}!`,
                2, p.od, p.do,
                { Tip: 'Nedeljni odmor', Podtip: 'Nenadoknađen', Vreme: p.do,
                  Trajanje: p.trajanje, Manjak: manjak }));
        }
    });

    // razmak između dva nedeljna odmora — najviše 6 × 24h
    for (let i = 1; i < nedeljni.length; i++) {
        const prethodni = nedeljni[i - 1];
        const tekuci = nedeljni[i];
        const razmak = tekuci.od - prethodni.krajBloka;
        if (razmak > L.nedeljniNajkasnije) {
            const rok = prethodni.krajBloka + L.nedeljniNajkasnije;
            out.push(kontekst.prekrsaj(
                `Prekršaj dana ${fmtDatum(tekuci.od)}: kasno započet nedeljni odmor `
                + `(AETR čl. 8 — najkasnije posle šest 24-časovnih perioda od kraja prethodnog nedeljnog odmora). `
                + `Trebao je početi do ${fmtDatumVreme(rok)}, a započet je ${fmtDatumVreme(tekuci.od)} — kasnio ${fmtTrajanje(razmak - L.nedeljniNajkasnije)}!`,
                2, rok, tekuci.od,
                { Tip: 'Nedeljni odmor', Podtip: 'Kasno započet', Vreme: tekuci.od,
                  Prekoracenje: razmak - L.nedeljniNajkasnije }));
        }
    }

    // od poslednjeg nedeljnog odmora do kraja perioda izveštaja
    const poslednji = nedeljni[nedeljni.length - 1];
    const kraj = dateTo instanceof Date ? dateTo.getTime() : (dateTo ? new Date(dateTo).getTime() : null);
    const krajPodataka = periodi.length ? periodi[periodi.length - 1].do : null;
    const granica = Math.min(kraj || Infinity, krajPodataka || Infinity);

    if (poslednji && isFinite(granica) && granica - poslednji.krajBloka > L.nedeljniNajkasnije) {
        const rok = poslednji.krajBloka + L.nedeljniNajkasnije;
        out.push(kontekst.prekrsaj(
            `Prekršaj dana ${fmtDatum(granica)}: nema nedeljnog odmora `
            + `(AETR čl. 8 — najkasnije posle šest 24-časovnih perioda od kraja prethodnog nedeljnog odmora). `
            + `Rok je bio ${fmtDatumVreme(rok)}, a do ${fmtDatumVreme(granica)} nema odmora dužeg od ${fmtTrajanje(L.nedeljniOdmorSkraceni)}!`,
            2, rok, granica,
            { Tip: 'Nedeljni odmor', Podtip: 'Nema odmora', Vreme: granica,
              Prekoracenje: granica - rok }));
    }

    if (!poslednji && periodi.length && isFinite(granica)
        && granica - periodi[0].od > L.nedeljniNajkasnije) {
        out.push(kontekst.prekrsaj(
            `Prekršaj dana ${fmtDatum(granica)}: nema nedeljnog odmora u celom periodu `
            + `(AETR čl. 8 — potreban odmor od najmanje ${fmtTrajanje(L.nedeljniOdmorSkraceni)})!`,
            2, periodi[0].od, granica,
            { Tip: 'Nedeljni odmor', Podtip: 'Nema odmora u periodu', Vreme: granica }));
    }

    return out;
}

/** Čl. 6 — nedeljno (56h) i dvonedeljno (90h) vreme upravljanja. */
function praviloNedeljnaVoznja(periodi, kontekst) {
    const L = kontekst.L;
    const out = [];
    const poNedelji = new Map();

    periodi.filter(p => p.tip === 'RAD' && p.voznja > 0).forEach(p => {
        const k = isoNedeljaKljuc(p.od);
        const n = poNedelji.get(k) || { voznja: 0, od: p.od, do: p.do };
        n.voznja += p.voznja;
        n.od = Math.min(n.od, p.od);
        n.do = Math.max(n.do, p.do);
        poNedelji.set(k, n);
    });

    const kljucevi = [...poNedelji.keys()].sort((a, b) => a - b);

    kljucevi.forEach((k, idx) => {
        const n = poNedelji.get(k);
        if (n.voznja > L.nedeljnaVoznja) {
            out.push(kontekst.prekrsaj(
                `Prekršaj u nedelji ${fmtDatum(n.od)} – ${fmtDatum(n.do)}: prekoračeno nedeljno vreme vožnje `
                + `(AETR čl. 6 — najviše ${fmtTrajanje(L.nedeljnaVoznja)}). Vožnja ${fmtTrajanje(n.voznja)}!`,
                nivoNedeljnaVoznja(n.voznja), n.od, n.do,
                { Tip: 'Vreme vožnje', Podtip: 'Nedeljno', Vreme: n.do, Trajanje: n.voznja,
                  Prekoracenje: n.voznja - L.nedeljnaVoznja }));
        }
        // dvonedeljno — samo za uzastopne nedelje
        const prethodniK = kljucevi[idx - 1];
        if (prethodniK !== undefined) {
            const prethodna = poNedelji.get(prethodniK);
            const zbir = n.voznja + prethodna.voznja;
            if (zbir > L.dvonedeljnaVoznja) {
                out.push(kontekst.prekrsaj(
                    `Prekršaj u periodu ${fmtDatum(prethodna.od)} – ${fmtDatum(n.do)}: prekoračeno dvonedeljno vreme vožnje `
                    + `(AETR čl. 6 — najviše ${fmtTrajanje(L.dvonedeljnaVoznja)} u dve uzastopne nedelje). Vožnja ${fmtTrajanje(zbir)}!`,
                    nivoDvonedeljnaVoznja(zbir), prethodna.od, n.do,
                    { Tip: 'Vreme vožnje', Podtip: 'Dvonedeljno', Vreme: n.do, Trajanje: zbir,
                      Prekoracenje: zbir - L.dvonedeljnaVoznja }));
            }
        }
    });

    return out;
}

/**
 * Dnevno radno vreme (čl. 4): najviše 14h u 24h, a ako se obavlja noćni rad
 * (bilo koji rad ili vožnja između 00:00 i 04:00, čl. 40–41) — najviše 10h.
 * Radno vreme = vožnja + ostali rad + pauze kraće od 15 min.
 */
function praviloRadnoVreme(periodi, kontekst) {
    const L = kontekst.L;
    const out = [];

    periodi.filter(p => p.tip === 'RAD').forEach(p => {
        const segmenti = segmentiUPeriodu(kontekst.aktivnosti, p.od, p.do);
        const nocniMs = segmenti
            .filter(s => jeVoznja(s.v) || jeRad(s.v))
            .reduce((a, s) => a + nocniPreklop(s.od, s.do), 0);
        const imaNocni = nocniMs > 0;
        const limit = imaNocni ? RADNO_VREME_NOCNO : RADNO_VREME_MAX;

        const ukupno = radnoVreme(segmenti);
        if (ukupno <= limit) return;

        // trenutak u kome je limit probijen
        let akum = 0, trenutak = p.do;
        for (const s of segmenti) {
            const doprinos = (jeVoznja(s.v) || jeRad(s.v) || (jeOdmor(s.v) && s.t < KRATKA_PAUZA)) ? s.t : 0;
            if (doprinos && akum + doprinos >= limit) {
                trenutak = s.od + (limit - akum);
                break;
            }
            akum += doprinos;
        }

        const osnov = imaNocni
            ? `najviše ${fmtTrajanje(RADNO_VREME_NOCNO)} u 24h jer se obavlja noćni rad `
              + `(${fmtTrajanje(nocniMs)} u periodu ${String(NOCNO_OD).padStart(2, '0')}:00–${String(NOCNO_DO).padStart(2, '0')}:00)`
            : `najviše ${fmtTrajanje(RADNO_VREME_MAX)} u periodu od 24 časa`;

        out.push(kontekst.prekrsaj(
            `Prekršaj dana ${fmtDatum(p.do)}: prekoračeno dnevno radno vreme `
            + `(Zakon o radnom vremenu posade vozila, čl. 4 — ${osnov}; `
            + `pauze kraće od ${Math.round(KRATKA_PAUZA / MIN)} min računaju se u radno vreme). `
            + `Radno vreme ${fmtTrajanje(ukupno)}!`,
            3, p.od, p.do,
            { Tip: 'Radno vreme',
              Podtip: imaNocni ? 'Noćni rad (10h)' : 'Dnevno (14h)',
              Vreme: trenutak, Trajanje: ukupno, Prekoracenje: ukupno - limit }));
    });

    return out;
}

/* ── javni API ───────────────────────────────────────────────────────────── */

const PRAVILA = {
    dayRest:    (ctx) => praviloDnevniOdmor(ctx.periodi, ctx),
    sumDriving: (ctx) => praviloDnevnaVoznja(ctx.periodi, ctx),
    less45:     (ctx) => praviloPauza(ctx.aktivnosti, ctx),
    weekRest:   (ctx) => praviloNedeljniOdmor(ctx.periodi, ctx, ctx.dateTo),
    twoWeek:    (ctx) => praviloNedeljnaVoznja(ctx.periodi, ctx),
    workTime:   (ctx) => praviloRadnoVreme(ctx.periodi, ctx),
};

/**
 * Računa prekršaje za sve vozače iz zapisa.
 *
 * @param {Array}    records   aktivnosti sa /tacho/faults
 * @param {string}   companyName
 * @param {Date}     dateTo
 * @param {string[]} propisi   ključevi iz PROPISI; prazno → svi. Puštaju se SAMO
 *                             pravila izabranih propisa, a limiti se uzimaju iz njih —
 *                             pa nova revizija (npr. AETR 2020) ne dira ovaj fajl.
 * @returns {Object} nizovi prekršaja po ključu pravila
 */
export function izracunajPoPravilima({ records, companyName, dateTo, propisi, idDrzava }) {
    const izabrani = Array.isArray(propisi) && propisi.length ? propisi : SVI_PROPISI;
    /* idDrzava je kapija: propis koji ne važi u toj državi izlazi iz obračuna čak i
       ako ga je pozivalac izričito tražio (vidi propisiZa u propisi.js). */
    const L = limitiZa(izabrani, idDrzava);
    const dozvoljena = pravilaZa(izabrani, idDrzava);
    /* Rezultat uvek nosi SVE ključeve, i prazne — pozivalac tako ne mora da zna
       koji su propisi izabrani da bi pročitao odgovor. */
    const rezultat = Object.keys(PRAVILA).reduce((a, k) => { a[k] = []; return a; }, {});
    const zapisi = Array.isArray(records) ? records : [];
    if (zapisi.length === 0) return rezultat;

    const poVozacu = new Map();
    zapisi.forEach(r => {
        if (!poVozacu.has(r.DriverId)) poVozacu.set(r.DriverId, { ime: r.HolderName, aktivnosti: [] });
        poVozacu.get(r.DriverId).aktivnosti.push(r);
    });

    poVozacu.forEach(({ ime, aktivnosti }) => {
        const periodi = napraviPeriode(aktivnosti, L);
        const ctx = {
            periodi,
            aktivnosti,
            dateTo,
            L,
            /* meta: { Tip, Podtip, Vreme, Trajanje, Prekoracenje, Manjak } — daje
               strukturisan prikaz (tabela) pored pune tekstualne poruke. */
            prekrsaj: (poruka, nivo, od, doo, meta = {}) => {
                /* Propis, kategorija i broj člana se izvode iz registra, da svaki
                   prekršaj zna po čemu je prekršaj — i da izveštaj može da se
                   grupiše kao kontrolni izveštaj, bez pogađanja po tekstu poruke. */
                const kat = kategorijaZa(meta.Podtip);
                return {
                    Vozač: ime,
                    Kompanija: companyName || '',
                    Poruka: saKaznom(poruka, nivo),
                    Pocetak: od != null ? new Date(od).toISOString() : null,
                    Kraj: doo != null ? new Date(doo).toISOString() : null,
                    Tip: meta.Tip || null,
                    Podtip: meta.Podtip || null,
                    Vreme: meta.Vreme != null ? new Date(meta.Vreme).toISOString()
                         : (doo != null ? new Date(doo).toISOString() : null),
                    Trajanje: meta.Trajanje ?? null,
                    Prekoracenje: meta.Prekoracenje ?? null,
                    Manjak: meta.Manjak ?? null,
                    Nivo: nivo,
                    Propis: kat ? kat.propis : null,
                    Kategorija: kat ? kat.naziv : null,
                    KategorijaRedni: kat ? kat.redni : null,
                    Clan: clanZa(meta.Podtip, nivo),
                };
            },
        };
        Object.keys(PRAVILA)
            .filter(k => dozvoljena.includes(k))
            .forEach(k => { rezultat[k] = rezultat[k].concat(PRAVILA[k](ctx)); });
    });

    // hronološki, da redosled u tabeli prati vreme nastanka
    Object.keys(rezultat).forEach(k => {
        rezultat[k].sort((a, b) => new Date(a.Pocetak || 0) - new Date(b.Pocetak || 0));
    });

    return rezultat;
}
